import React from 'react';
import {BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import TopBar from './common/components/TopBar';
import Places from './admin/artisticobjects/pages/Places';
import PlacesList from './admin/artisticobjects/components/PlacesList';
import PlaceForm from './admin/artisticobjects/components/PlaceForm';
import ArtisticObjects from './admin/artisticobjects/pages/ArtisticObjects';
import ArtisticObjectsList from './admin/artisticobjects/components/ArtisticObjectsList';
import ArtisticObjectsForm from './admin/artisticobjects/components/ArtisticObjectsForm';
import About from './general/pages/About';
import Partners from './general/pages/Partners';
import MCBW from './general/pages/MCBW';
import PrivacyPolicy from './general/pages/PrivacyPolicy';
import TermsOfService from './general/pages/TermsOfService';
import Login from './general/pages/Login';
import ProtectedRoute from './common/components/ProtectedRoute';
import {AuthProvider} from './common/contexts/AuthContext';
import {SearchProvider} from './common/contexts/SearchContext';
import {FavouritesProvider} from './common/contexts/FavouritesContext';
import Register from "./general/pages/Register";
import RegistrationConfirmation from "./general/pages/RegistrationConfirmation";
import EmailConfirmation from "./general/pages/EmailConfirmation";
import ForgotPassword from "./general/pages/ForgotPassword";
import ForgotPasswordConfirmation from "./general/pages/ForgotPasswordConfirmation";
import ResetPassword from "./general/pages/ResetPassword";
import Contact from "./general/pages/Contact";
import ContactConfirmation from "./general/pages/ContactConfirmation";
import BannerBar from "./common/components/BannerBar";
import Footer from "./common/components/Footer";
import PlaceSections from './admin/core/pages/PlaceSections';
import ArtisticObjectSections from './admin/core/pages/ArtisticObjectSections';
import DictionaryItemsListWrapper from './admin/core/components/DictionaryItemsListWrapper';
import DictionaryItemsFormWrapper from './admin/core/components/DictionaryItemsFormWrapper';
import OrganizationalUnits from "./admin/artisticobjects/pages/OrganizationalUnits";
import OrganizationalUnitsList from "./admin/artisticobjects/components/OrganizationalUnitsList";
import OrganizationalUnitsForm from "./admin/artisticobjects/components/OrganizationalUnitsForm";
import People from "./admin/artisticobjects/pages/People";
import PeopleList from "./admin/artisticobjects/components/PeopleList";
import PersonForm from "./admin/artisticobjects/components/PersonForm";
import Workshops from "./admin/artisticobjects/pages/Workshops";
import WorkshopsList from "./admin/artisticobjects/components/WorkshopsList";
import WorkshopForm from "./admin/artisticobjects/components/WorkshopForm";
import Publications from "./admin/publications/pages/Publications";
import PublicationsList from "./admin/publications/components/PublicationsList";
import PublicationForm from "./admin/publications/components/PublicationForm";
import Archives from "./admin/publications/pages/Archives";
import ArchivesList from "./admin/publications/components/ArchivesList";
import ArchiveForm from "./admin/publications/components/ArchiveForm";
import BibliographyItems from "./admin/publications/pages/BibliographyItems";
import BibliographyItemsList from "./admin/publications/components/BibliographyItemsList";
import BibliographyItemForm from "./admin/publications/components/BibliographyItemForm";
import Collections from "./admin/publications/pages/Collections";
import CollectionsList from "./admin/publications/components/CollectionsList";
import CollectionForm from "./admin/publications/components/CollectionForm";
import Tiles from "./admin/publications/pages/Tiles";
import TilesList from "./admin/publications/components/TilesList";
import TileForm from "./admin/publications/components/TileForm";
import Pages from "./admin/publications/pages/Pages";
import PagesList from "./admin/publications/components/PagesList";
import PageForm from "./admin/publications/components/PageForm";
import Gallery from "./gallery/pages/Gallery";
import ArtisticObjectDetails from "./gallery/pages/ArtisticObjectDetails";
import PlaceDetails from "./gallery/pages/PlaceDetails";
import HomePage from "./general/pages/HomePage";
import PlacesListing from "./locations/pages/PlacesListing";
import CollectionsListing from "./gallery/pages/CollectionsListing";
import CollectionView from "./gallery/pages/CollectionView";
import Users from "./admin/users/pages/Users";
import UsersList from "./admin/users/components/UsersList";
import UserForm from "./admin/users/components/UserForm";
import PageView from "./general/pages/PageView";
import {UserRole} from "./admin/users/types/User";

import {ToastProvider} from './common/contexts/ToastContext';
import {ConfirmDialog} from 'primereact/confirmdialog';

const App: React.FC = () => {
  return (
    <ToastProvider>
      <FavouritesProvider>
        <AuthProvider>
          <SearchProvider>
            <Router>
              <TopBar/>
              <BannerBar/>
              <main className="">
                <Routes>
                  <Route path="/" element={<About/>}/>
                  <Route path="/about" element={<About/>}/>
                  <Route path="/partners" element={<Partners/>}/>
                  <Route path="/mcbw" element={<MCBW/>}/>
                  <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                  <Route path="/terms-of-service" element={<TermsOfService/>}/>
                  <Route path="/home" element={<HomePage/>}/>
                  <Route path="/login" element={<Login/>}/>
                  <Route path="/register" element={<Register/>}/>
                  <Route path="/registration-confirmation" element={<RegistrationConfirmation/>}/>
                  <Route path="/confirm-email" element={<EmailConfirmation/>}/>
                  <Route path="/forgot-password" element={<ForgotPassword/>}/>
                  <Route path="/forgot-password-confirmation" element={<ForgotPasswordConfirmation/>}/>
                  <Route path="/reset-password" element={<ResetPassword/>}/>
                  <Route path="/contact" element={<Contact/>}/>
                  <Route path="/contact-confirmation" element={<ContactConfirmation/>}/>
                  <Route path="/gallery" element={<Gallery/>}/>
                  <Route path="/gallery/artistic-object/:id" element={<ArtisticObjectDetails/>}/>
                  <Route path="/gallery/place/:id" element={<PlaceDetails/>}/>
                  <Route path="/locations" element={<PlacesListing/>}/>
                  <Route path="/browse-collections" element={<CollectionsListing/>}/>
                  <Route path="/browse-collections/:id" element={<CollectionView/>}/>
                  <Route path="/page/:alias" element={<PageView/>}/>
                  <Route path="/places/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<Places/>}>
                          <Route index element={<PlacesList/>}/>
                          <Route path="new" element={<PlaceForm/>}/>
                          <Route path=":id/edit" element={<PlaceForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/organizational-units/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<OrganizationalUnits/>}>
                          <Route index element={<OrganizationalUnitsList/>}/>
                          <Route path="new" element={<OrganizationalUnitsForm/>}/>
                          <Route path=":id/edit" element={<OrganizationalUnitsForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/people/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<People/>}>
                          <Route index element={<PeopleList/>}/>
                          <Route path="new" element={<PersonForm/>}/>
                          <Route path=":id/edit" element={<PersonForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/workshops/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<Workshops/>}>
                          <Route index element={<WorkshopsList/>}/>
                          <Route path="new" element={<WorkshopForm/>}/>
                          <Route path=":id/edit" element={<WorkshopForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/publications/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<Publications/>}>
                          <Route index element={<PublicationsList/>}/>
                          <Route path="new" element={<PublicationForm/>}/>
                          <Route path=":id/edit" element={<PublicationForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/archives/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<Archives/>}>
                          <Route index element={<ArchivesList/>}/>
                          <Route path="new" element={<ArchiveForm/>}/>
                          <Route path=":id/edit" element={<ArchiveForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/bibliography-items/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<BibliographyItems/>}>
                          <Route index element={<BibliographyItemsList/>}/>
                          <Route path="new" element={<BibliographyItemForm/>}/>
                          <Route path=":id/edit" element={<BibliographyItemForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/collections/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<Collections/>}>
                          <Route index element={<CollectionsList/>}/>
                          <Route path="new" element={<CollectionForm/>}/>
                          <Route path=":id/edit" element={<CollectionForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/tiles/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<Tiles/>}>
                          <Route index element={<TilesList/>}/>
                          <Route path="new" element={<TileForm/>}/>
                          <Route path=":id/edit" element={<TileForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/pages/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<Pages/>}>
                          <Route index element={<PagesList/>}/>
                          <Route path="new" element={<PageForm/>}/>
                          <Route path=":id/edit" element={<PageForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/artistic-objects/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.REDACTOR]}>
                      <Routes>
                        <Route path="/" element={<ArtisticObjects/>}>
                          <Route index element={<ArtisticObjectsList/>}/>
                          <Route path="new" element={<ArtisticObjectsForm/>}/>
                          <Route path=":id/edit" element={<ArtisticObjectsForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/place-sections/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                      <Routes>
                        <Route path="/" element={<PlaceSections/>}>
                          <Route index element={<DictionaryItemsListWrapper/>}/>
                          <Route path="new" element={<DictionaryItemsFormWrapper/>}/>
                          <Route path=":alias/edit" element={<DictionaryItemsFormWrapper/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/artistic-object-sections/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                      <Routes>
                        <Route path="/" element={<ArtisticObjectSections/>}>
                          <Route index element={<DictionaryItemsListWrapper/>}/>
                          <Route path="new" element={<DictionaryItemsFormWrapper/>}/>
                          <Route path=":alias/edit" element={<DictionaryItemsFormWrapper/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                  <Route path="/users/*" element={
                    <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                      <Routes>
                        <Route path="/" element={<Users/>}>
                          <Route index element={<UsersList/>}/>
                          <Route path=":id/edit" element={<UserForm/>}/>
                        </Route>
                      </Routes>
                    </ProtectedRoute>
                  }/>
                </Routes>
              </main>
              <Footer/>
            </Router>
          </SearchProvider>
        </AuthProvider>
      </FavouritesProvider>
      <ConfirmDialog
        acceptClassName="px-6 py-3 bg-black text-white rounded-sm hover:bg-gray-800 transition duration-200 ml-4 font-normal text-base border-none"
        rejectClassName="px-6 py-3 border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-50 transition duration-200 bg-white font-normal text-base"
        maskClassName="bg-black bg-opacity-50"
      />
    </ToastProvider>
  );
};

export default App;
